<template>
  <div
    id="vacation-pending"
    class="block block-rounded mb-4"
  >
    <div class="block-header block-header-default">
      <h3 class="block-title">
        Pending Vacation Requests
      </h3>
    </div>
    <div class="block-content">
      <div
        v-if="pendingList.length"
        class="row g-3"
      >
        <div
          v-for="vacation in pendingList"
          :key="vacation.id"
          class="col-12 col-md-6 col-lg-4"
        >
          <div class="card h-100">
            <div class="card-body py-3">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <span class="fw-semibold">{{ vacation.start_date }} – {{ vacation.end_date }}</span>
                <span
                  class="badge"
                  :class="statusBadgeClass(vacation)"
                >
                  <i
                    :class="statusIcon(vacation)"
                    class="me-1"
                    aria-hidden="true"
                  />
                  {{ getStatus(vacation) }}
                </span>
              </div>
              <div class="text-muted small mb-2">
                {{ showType(vacation) }} · {{ displayDays(vacation) }} days
              </div>
              <div
                v-if="vacation.reason"
                class="small text-truncate mb-2"
                :title="vacation.reason"
              >
                {{ vacation.reason }}
              </div>
              <div class="btn-group btn-group-sm">
                <button
                  v-if="!isSubmitted(vacation)"
                  type="button"
                  class="btn btn-success"
                  :disabled="submitting"
                  @click="submitRequest(vacation.id)"
                >
                  Submit
                </button>
                <button
                  v-if="!vacation.is_rejected"
                  type="button"
                  class="btn btn-warning"
                  :disabled="submitting"
                  @click="cancelRequest(vacation.id)"
                >
                  Cancel
                </button>
                <button
                  v-if="isSubmitted(vacation) && !vacation.is_approved"
                  type="button"
                  class="btn btn-info"
                  :disabled="submitting || reminding"
                  @click="remindRequest(vacation.id)"
                >
                  Remind
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="text-center py-4 text-muted"
      >
        <p class="mb-2">No pending requests</p>
        <slot name="empty-action">
          <span>Request leave to get started</span>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "VacationPendingRequests",
    props: {
        staffId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            submitting: false,
            reminding: false,
            calculatedDays: {},
            calculatingDays: [],
        };
    },
    computed: {
        vacationList() {
            return this.$store.getters["vacation/vacationList"];
        },
        sickLeaveList() {
            return this.$store.getters["vacation/sickLeaveList"];
        },
        pendingList() {
            const isPending = (v) => v.is_approved !== 1 && !v.is_rejected;
            const vacation = this.vacationList.filter(isPending);
            const sick = this.sickLeaveList.filter(isPending);
            return [...vacation, ...sick].sort((a, b) => a.start_date.localeCompare(b.start_date));
        },
    },
    methods: {
        getStatus(vacation) {
            if (vacation.is_approved === 1) return "Approved";
            if (vacation.is_rejected) return "Rejected";
            return vacation.submitted === 1 ? "Submitted" : "Draft";
        },
        statusBadgeClass(vacation) {
            if (vacation.is_approved === 1) return "bg-success";
            if (vacation.is_rejected) return "bg-danger";
            return vacation.submitted === 1 ? "bg-info" : "bg-secondary";
        },
        statusIcon(vacation) {
            if (vacation.is_approved === 1) return "fa fa-check";
            if (vacation.is_rejected) return "fa fa-times";
            return vacation.submitted === 1 ? "fa fa-clock" : "fa fa-edit";
        },
        showType(vacation) {
            return vacation.sick_leave === 1 ? "Sick Leave" : "Vacation";
        },
        isSubmitted(vacation) {
            return vacation.submitted === 1;
        },
        displayDays(vacation) {
            if (this.calculatedDays[vacation.id] !== undefined) return this.calculatedDays[vacation.id];
            if (this.calculatingDays.includes(vacation.id)) return "...";
            return vacation.total_days ?? "—";
        },
        async submitRequest(id) {
            if (!confirm("Do you want to submit this request?")) return;
            try {
                this.submitting = true;
                await this.submitVacationRequest({ id, type: "submit" });
                alert("Leave request submitted");
                this.$emit("update");
            } catch (e) {
                alert("Error occurred");
                console.error(e);
            } finally {
                this.submitting = false;
            }
        },
        async cancelRequest(id) {
            if (!confirm("Do you want to cancel this request?")) return;
            try {
                this.submitting = true;
                await this.cancelVacationRequest({ id });
                alert("Request cancelled");
                this.$emit("update");
            } catch (e) {
                alert("Error occurred");
                console.error(e);
            } finally {
                this.submitting = false;
            }
        },
        async remindRequest(id) {
            if (!confirm("Do you want to send a reminder?")) return;
            try {
                this.reminding = true;
                await this.submitVacationRequest({ id, type: "remind" });
                alert("Reminder sent");
                this.$emit("update");
            } catch (e) {
                alert("Error occurred");
                console.error(e);
            } finally {
                this.reminding = false;
            }
        },
        ...mapActions({
            submitVacationRequest: "vacation/submitRequest",
            cancelVacationRequest: "vacation/cancelRequest",
        }),
    },
};
</script>
